import { useState, useEffect } from 'react';
import type { TransferDestination } from '@/types';
import { apiService } from '@services/api.service';
import { useStore } from '@services/store';

/**
 * Page de transfert affichée dans une iframe Crisp (ou popup en dev)
 */
export const TransferPage = () => {
  const { session } = useStore();
  const [destinations, setDestinations] = useState<TransferDestination[]>([]);
  const [selectedDestination, setSelectedDestination] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Charger les destinations au montage
  useEffect(() => {
    loadDestinations();
  }, []);

  const loadDestinations = async () => {
    try {
      setLoadingDestinations(true);
      setError(null);
      const data = await apiService.getTransferDestinations();
      setDestinations(data);
    } catch (err) {
      setError('Erreur lors du chargement des destinations');
      console.error('Error loading destinations:', err);
    } finally {
      setLoadingDestinations(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDestination) {
      setError('Veuillez sélectionner une destination');
      return;
    }

    if (!session?.sessionId) {
      setError('Session non trouvée');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiService.transferTicket({
        sessionId: session.sessionId,
        destination: selectedDestination,
        comment: comment.trim() || undefined,
        operatorId: session.operatorId || 'unknown',
        operatorName: session.operatorName || 'Opérateur',
      });

      setSuccess(true);

      // Fermer la popup/iframe après 1.5s
      setTimeout(() => {
        console.log('🔄 Attempting to close modal...');

        // Essayer de fermer la modal Crisp directement
        try {
          if ((window as any).$crisp?.closeModal) {
            console.log('✅ Closing via window.$crisp.closeModal()');
            (window as any).$crisp.closeModal();
          } else if (window.parent && (window.parent as any).$crisp?.closeModal) {
            console.log('✅ Closing via window.parent.$crisp.closeModal()');
            (window.parent as any).$crisp.closeModal();
          } else if (window.top && (window.top as any).$crisp?.closeModal) {
            console.log('✅ Closing via window.top.$crisp.closeModal()');
            (window.top as any).$crisp.closeModal();
          } else {
            console.log('⚠️ $crisp.closeModal() not found, trying postMessage');
            // Fallback: envoyer un message au parent et tous les niveaux supérieurs
            window.parent?.postMessage({ type: 'TRANSFER_COMPLETE' }, '*');
            window.top?.postMessage({ type: 'TRANSFER_COMPLETE' }, '*');
          }
        } catch (error) {
          console.error('❌ Error closing modal:', error);
          // Fallback en cas d'erreur CORS
          try {
            window.parent?.postMessage({ type: 'TRANSFER_COMPLETE' }, '*');
          } catch (e) {
            console.error('❌ Error sending postMessage:', e);
          }
        }

        // Si on est dans une popup (mode dev)
        if (window.opener) {
          window.opener.postMessage({ type: 'TRANSFER_COMPLETE' }, '*');
          window.close();
        }
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du transfert');
      console.error('Transfer error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    console.log('🔄 Cancel button clicked, attempting to close modal...');

    // Essayer de fermer la modal Crisp directement
    try {
      if ((window as any).$crisp?.closeModal) {
        console.log('✅ Closing via window.$crisp.closeModal()');
        (window as any).$crisp.closeModal();
      } else if (window.parent && (window.parent as any).$crisp?.closeModal) {
        console.log('✅ Closing via window.parent.$crisp.closeModal()');
        (window.parent as any).$crisp.closeModal();
      } else if (window.top && (window.top as any).$crisp?.closeModal) {
        console.log('✅ Closing via window.top.$crisp.closeModal()');
        (window.top as any).$crisp.closeModal();
      } else {
        console.log('⚠️ $crisp.closeModal() not found, trying postMessage');
        // Fallback: envoyer un message au parent
        window.parent?.postMessage({ type: 'TRANSFER_CANCEL' }, '*');
        window.top?.postMessage({ type: 'TRANSFER_CANCEL' }, '*');
      }
    } catch (error) {
      console.error('❌ Error closing modal:', error);
      // Fallback en cas d'erreur CORS
      try {
        window.parent?.postMessage({ type: 'TRANSFER_CANCEL' }, '*');
      } catch (e) {
        console.error('❌ Error sending postMessage:', e);
      }
    }

    // Si on est dans une popup (mode dev)
    if (window.opener) {
      window.close();
    }
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-crisp-dark mb-1">
            📤 Transférer la conversation
          </h1>
          <p className="text-sm text-crisp-gray">
            Transférez ce ticket à une autre équipe ou personne
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-lg">✓</span>
              <p className="text-sm text-green-800 font-medium">
                Transfert effectué avec succès
              </p>
            </div>
            <p className="text-xs text-green-700 mt-1 ml-7">
              Fermeture automatique...
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-red-600 text-lg">⚠</span>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Destination Select */}
          <div>
            <label htmlFor="destination" className="label">
              Destination *
            </label>
            {loadingDestinations ? (
              <div className="flex items-center gap-2 p-3 border border-crisp-border rounded-lg">
                <div className="w-4 h-4 border-2 border-crisp-blue border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-crisp-gray">Chargement des destinations...</span>
              </div>
            ) : destinations.length === 0 ? (
              <div className="p-3 border border-crisp-border rounded-lg">
                <p className="text-sm text-crisp-gray">Aucune destination disponible</p>
              </div>
            ) : (
              <select
                id="destination"
                value={selectedDestination}
                onChange={(e) => setSelectedDestination(e.target.value)}
                disabled={loading || success}
                required
                className="w-full px-4 py-2.5 border border-crisp-border rounded-lg focus:outline-none focus:ring-2 focus:ring-crisp-blue focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-sm bg-white"
              >
                <option value="">Sélectionner une destination</option>
                {destinations.map((dest) => (
                  <option key={dest.name} value={dest.name}>
                    {dest.name}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1.5 text-xs text-crisp-gray">
              Choisissez la personne ou l'équipe qui recevra ce ticket
            </p>
          </div>

          {/* Comment Textarea */}
          <div>
            <label htmlFor="comment" className="label">
              Commentaire (optionnel)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={loading || success}
              rows={5}
              placeholder="Ajoutez des informations utiles pour la personne qui recevra ce transfert..."
              className="w-full px-4 py-2.5 border border-crisp-border rounded-lg focus:outline-none focus:ring-2 focus:ring-crisp-blue focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-sm resize-none"
            />
            <div className="mt-1.5 flex items-center justify-between">
              <p className="text-xs text-crisp-gray">
                Expliquez le contexte ou les actions déjà effectuées
              </p>
              <p className="text-xs text-crisp-gray-light">
                {comment.length} caractères
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading || success}
              className="flex-1 px-4 py-2.5 border border-crisp-border text-crisp-gray-dark rounded-lg hover:bg-crisp-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || loadingDestinations || !selectedDestination || success}
              className="flex-1 px-4 py-2.5 bg-crisp-blue text-white rounded-lg hover:bg-crisp-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Transfert...</span>
                </>
              ) : (
                <>
                  <span>📤</span>
                  <span>Transférer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
